import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useForm, z, zodResolver } from "@/hooks/schema";
import { Form } from "@/components/Form";
import { Check, Pencil, ImageUp, Trash } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { getShowSellers, updateSellersData } from "@/Api/sellers";
import { useToastNavigate } from "@/utils/toastNavigate";
import { LoaderSpin } from "@/components/Loaders";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Mask } from "@/utils/mask";
import { getSelectUnities } from "@/Api/unities";
import { getCet } from "@/Api/cep";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormSteps, useStepControl } from "@/components/Form/Steps";
import { TypeUser } from "@/types/userType";
import { useAuth } from "@/hooks/auth";

const step1Schema = z.object({
  name: z.string({ message: "*requerido" }).min(1, "*requerido"),
  nickname: z.string().optional(),
  email: z
    .string({ message: "*requerido" })
    .min(1, "*requerido")
    .email("Email inválido"),
  password: z.string().optional(),
  document: z.string({ message: "*requerido" }).min(1, "*requerido"),
  phone: z.string({ message: "*requerido" }).min(1, "*requerido"),
  photo: z.any(),
  unity_id: z.any({ message: "*requerido" }),
});

const step2Schema = z.object({
  street: z.string().min(1, "*requerido").max(255),
  number: z.string().min(1, "*requerido").max(255),
  neighborhood: z.string().min(1, "*requerido").max(255),
  complement: z.string().max(255).optional(),
  state: z.string().min(1, "*requerido").max(255),
  city: z.string().min(1, "*requerido").max(255),
  zip_code: z.string().min(1, "*requerido").max(255),
});

const schema = z.object({
  ...step1Schema.shape,
  ...step2Schema.shape,
});

const schemas = {
  1: step1Schema,
  2: step2Schema,
};

type SchemaType = z.infer<typeof schema>;

export const UpdateBtn = ({
  updateFront,
  uuid,
}: {
  updateFront: (response: any) => void;
  uuid: string;
}) => {
  const user = useAuth();
  const unityId = user?.user?.unity?.[0]?.id || "";
  const userTypeUnitReponsible =
    user?.user?.user_type_id == TypeUser.unity_responsible;
  const stepControls = useStepControl();

  const form = useForm<SchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      nickname: "",
      email: "",
      password: undefined,
      document: "",
      phone: "",
      photo: null,
      unity_id: userTypeUnitReponsible ? unityId : "",
      street: "",
      number: "",
      neighborhood: "",
      complement: "",
      state: "",
      city: "",
      zip_code: "",
    },
  });

  const { toast } = useToastNavigate();
  const [formState, setFormState] = useState<string>("default");
  const [isOpen, setIsOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [unities, setUnities] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    const loadUnities = async () => {
      const { data } = await getSelectUnities();
      setUnities(data?.response);
    };
    loadUnities();
  }, [isOpen]);

  useEffect(() => {
    const fetchData = async () => {
      if (isOpen) {
        const { data: responseApi } = await getShowSellers(uuid);
        form.setValue("name", responseApi?.response?.user?.name || "");
        form.setValue("nickname", responseApi?.response?.nickname || "");
        form.setValue("email", responseApi?.response?.user?.email || "");
        form.setValue(
          "document",
          Mask.maskCpfOrCnpj(responseApi?.response?.document || "")
        );
        form.setValue(
          "phone",
          Mask.maskPhone(responseApi?.response?.phone || "")
        );
        form.setValue("photo", responseApi?.response?.user?.photo || null);
        form.setValue(
          "unity_id",
          String(responseApi?.response?.unity_id) || ""
        );
        form.setValue("street", responseApi?.response?.address?.street || "");
        form.setValue("number", responseApi?.response?.address?.number || "");
        form.setValue(
          "neighborhood",
          responseApi?.response?.address?.neighborhood || ""
        );
        form.setValue(
          "complement",
          responseApi?.response?.address?.complement || ""
        );
        form.setValue("state", responseApi?.response?.address?.state || "");
        form.setValue("city", responseApi?.response?.address?.city || "");
        form.setValue(
          "zip_code",
          responseApi?.response?.address?.zip_code || ""
        );
        setImagePreview(responseApi?.response?.user?.photo || null);
      }
    };

    fetchData();
  }, [isOpen, uuid, form]);

  const handleNextStep = async () => {
    const currentSchema = schemas[stepControls.step as keyof typeof schemas];
    const currentStepData = form.getValues();

    try {
      await currentSchema.parseAsync(currentStepData);
      stepControls.setStep(stepControls.step + 1);
    } catch (error) {
      form.trigger();
    }
  };

  const handleCepChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const cep = e.target.value.replace(/\D/g, "");

    if (cep.length === 8) {
      try {
        const { data } = await getCet(cep);
        if (!data.erro) {
          form.setValue("street", data.logradouro);
          form.setValue("neighborhood", data.bairro);
          form.setValue("city", data.localidade);
          form.setValue("state", data.uf);
        }
      } catch (error) {
        console.error("Erro ao buscar CEP:", error);
      }
    }
  };

  const formSubmit = async (data: SchemaType) => {
    try {
      setFormState("loading");
      const formData = new FormData();

      Object.keys(data).forEach((key) => {
        if (key === "photo") {
          if (data[key] instanceof File) {
            formData.append(key, data[key]);
          }
        } else {
          formData.append(key, data[key]);
        }
      });

      const { data: apiResponse, ok } = await updateSellersData(uuid, formData);
      if (!ok) {
        throw new Error("Erro ao atualizar vendedor");
      }

      updateFront(apiResponse?.response);
      setFormState("default");
      stepControls.setStep(1);
      form.reset();
      setIsOpen(false);
      toast({
        description: `Vendedor atualizado com sucesso!`,
        action: <Check color="#1edc2b" />,
      });
    } catch (error) {
      setFormState("error");
      toast({
        description: `Erro ao atualizar vendedor`,
        variant: "destructive",
      });

      setTimeout(() => {
        setFormState("default");
      }, 3000);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
          onClick={() => setIsOpen(true)}
        >
          <Pencil className="size-5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px] h-[90vh]">
        <DialogHeader className="flex flex-col gap-2">
          <DialogDescription className="text-lg">
            Editar Vendedor
          </DialogDescription>
        </DialogHeader>

        <FormSteps controls={stepControls}>
          <FormSteps.Header>
            <FormSteps.Indicator step={1}>
              Informações Básicas
            </FormSteps.Indicator>
            <FormSteps.Indicator step={2}>Endereço</FormSteps.Indicator>
          </FormSteps.Header>

          <ScrollArea className="h-[calc(90vh-10rem)] pr-4">
            <Form
              onSubmit={form.handleSubmit(formSubmit)}
              {...form}
              className="space-y-3 p-2"
            >
              <FormSteps.Content step={1}>
                <Form.Row>
                  <Form.Field name="photo">
                    {({ field }) => (
                      <Form.Item className="flex flex-col items-center gap-1">
                        <Form.Label>Perfil:</Form.Label>
                        <div className="flex items-center rounded-full size-32 border border-dashed border-gray-900/25 dark:border-gray-500 relative">
                          <div className="flex text-sm/6 text-gray-600 h-full w-full">
                            <label
                              htmlFor="file-upload"
                              className="w-full h-full flex items-center justify-center cursor-pointer font-semibold text-blue-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 group rounded-full overflow-hidden"
                            >
                              {imagePreview && (
                                <img
                                  className="absolute top-0 left-0 aspect-square object-contain w-full h-full -z-1 group-hover:brightness-75 transition rounded-full"
                                  src={imagePreview}
                                  alt="profile_image"
                                />
                              )}
                              <span className="flex gap-2 items-center relative z-50">
                                {!imagePreview ? (
                                  <ImageUp className="size-7 group-hover:text-blue-700" />
                                ) : (
                                  <ImageUp className="size-7 opacity-0 transition group-hover:opacity-100" />
                                )}
                              </span>
                              <input
                                id="file-upload"
                                type="file"
                                className="sr-only"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    field.onChange(file);
                                    setImagePreview(URL.createObjectURL(file));
                                  }
                                }}
                              />
                            </label>
                          </div>
                        </div>
                        {imagePreview && (
                          <Button
                            type="button"
                            className="h-6 px-0 bg-transparent text-slate font-medium hover:text-red-600 hover:bg-transparent flex gap-1"
                            onClick={() => {
                              form.setValue("photo", null);
                              setImagePreview(null);
                            }}
                          >
                            <Trash className="size-5" />
                            Remover
                          </Button>
                        )}
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="name">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Nome <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input {...field} placeholder="Insira o nome" />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="nickname">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Apelido <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input {...field} placeholder="Insira o apelido (opcional)" />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="email">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Email <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input
                            {...field}
                            placeholder="Insira o email"
                            type="email"
                          />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="password">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Senha <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input
                            {...field}
                            placeholder="Insira a senha"
                            type="password"
                          />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="document">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Documento <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input
                            {...field}
                            className="mt-1"
                            typeMask="cpfOrCnpj"
                            placeholder="Insira o número de documentação"
                          />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="phone">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Telefone <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input
                            {...field}
                            typeMask="phone"
                            placeholder="Insira o telefone"
                          />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                {!userTypeUnitReponsible && (
                  <Form.Row>
                    <Form.Field name="unity_id">
                      {({ field }) => (
                        <Form.Item>
                          <Form.Label>
                            Unidade <Form.Message />
                          </Form.Label>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione" />
                            </SelectTrigger>
                            <SelectContent>
                              {unities.map((unity: any) => (
                                <SelectItem
                                  key={unity.id}
                                  value={String(unity.id)}
                                >
                                  {unity.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </Form.Item>
                      )}
                    </Form.Field>
                  </Form.Row>
                )}
              </FormSteps.Content>

              <FormSteps.Content step={2}>
                <Form.Row>
                  <Form.Field name="zip_code">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          CEP <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input
                            {...field}
                            placeholder="CEP"
                            onChange={(e) => {
                              field.onChange(e);
                              handleCepChange(e);
                            }}
                          />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="street">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Rua <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input {...field} placeholder="Rua" />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>

                  <Form.Field name="number">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Número <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input {...field} placeholder="Número" />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="neighborhood">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Bairro <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input {...field} placeholder="Bairro" />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>

                  <Form.Field name="complement">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Complemento <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input {...field} placeholder="Complemento" />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>

                <Form.Row>
                  <Form.Field name="state">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Estado <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input {...field} placeholder="Estado" />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>

                  <Form.Field name="city">
                    {({ field }) => (
                      <Form.Item>
                        <Form.Label>
                          Cidade <Form.Message />
                        </Form.Label>
                        <Form.Control>
                          <Input {...field} placeholder="Cidade" />
                        </Form.Control>
                      </Form.Item>
                    )}
                  </Form.Field>
                </Form.Row>
              </FormSteps.Content>

              <DialogFooter>
                <div className="flex w-full gap-2">
                  {stepControls.step > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        stepControls.setStep(stepControls.step - 1)
                      }
                    >
                      Voltar
                    </Button>
                  )}

                  {stepControls.step < 2 && (
                    <Button
                      type="button"
                      className={cn("flex-1", colors.bg_primary)}
                      onClick={handleNextStep}
                    >
                      Próximo
                    </Button>
                  )}

                  {stepControls.step === 2 && (
                    <Button
                      type="submit"
                      className={cn("flex-1", colors.bg_primary)}
                      disabled={formState !== "default"}
                    >
                      {formState === "loading" && (
                        <>
                          Atualizando <LoaderSpin />
                        </>
                      )}
                      {formState === "default" && "Atualizar"}
                      {formState === "error" && "Erro ao atualizar"}
                    </Button>
                  )}
                </div>
              </DialogFooter>
            </Form>
          </ScrollArea>
        </FormSteps>
      </DialogContent>
    </Dialog>
  );
};
